import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useTranslation } from "react-i18next";

import { MissingRequirement } from "../../../../utils/requiredDocuments";

export const DOCUMENTS_SECTION_ID = "documents-section";

interface Props {
  open: boolean;
  items: MissingRequirement[];
  onClose: () => void;
}

// Shown when Submit to LOS is clicked with unmet document requirements:
// lists exactly what is missing and takes the user to the Documents section.
export default function MissingDocumentsDialog({ open, items, onClose }: Props) {
  const { t } = useTranslation();

  const goToDocuments = () => {
    onClose();
    // wait for the dialog to unmount so the scroll isn't interrupted
    setTimeout(
      () =>
        document
          .getElementById(DOCUMENTS_SECTION_ID)
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      150,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1.5, whiteSpace: "nowrap" }}
      >
        <ErrorOutlineIcon sx={{ color: "#B3261E" }} />
        {t("documentsRequiredTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{ fontFamily: "Poppins", fontSize: 14, color: "#3C3C3C", mb: 2 }}
        >
          {t("documentsRequiredDescription")}
        </Typography>
        <Box
          component="ul"
          data-testid="missing-documents-list"
          sx={{ listStyle: "none", m: 0, p: 0 }}
        >
          {items.map((item, index) => {
            const isMissing = item.code === "requiredDocMissing";
            return (
              <Box
                component="li"
                key={`${item.code}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.25,
                  borderTop: index ? "1px solid #EEF0F6" : "none",
                }}
              >
                <DescriptionOutlinedIcon sx={{ color: "#888" }} />
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: "Poppins",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {isMissing
                    ? (item.values as { label: string }).label
                    : t(item.code, item.values)}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 12,
                    px: 1,
                    py: 0.25,
                    borderRadius: "10px",
                    whiteSpace: "nowrap",
                    color: isMissing ? "#B3261E" : "#8A5A00",
                    background: isMissing ? "#FDECEA" : "#FFF3D6",
                  }}
                >
                  {isMissing ? t("notUploaded") : t("needsAttention")}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t("close")}</Button>
        <Button variant="contained" onClick={goToDocuments}>
          {t("goToDocuments")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
